import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react"

interface PageHeaderControllerContextType {
  setCustomPageHeader: (header: ReactNode | null) => void
  setNoPaddingBottom: (value: boolean) => void
  setHeaderBackgroundClassName: (className: string | null) => void
}

interface PageHeaderState {
  customPageHeader: ReactNode | null
  noPaddingBottom: boolean
  headerBackgroundClassName: string | null
}

interface PageHeaderStore {
  getSnapshot: () => PageHeaderState
  setCustomPageHeader: (header: ReactNode | null) => void
  setNoPaddingBottom: (value: boolean) => void
  setHeaderBackgroundClassName: (className: string | null) => void
  subscribe: (listener: () => void) => () => void
}

const PageHeaderControllerContext = createContext<
  PageHeaderControllerContextType | undefined
>(undefined)
const PageHeaderStoreContext = createContext<PageHeaderStore | undefined>(
  undefined,
)

interface PageHeaderProviderProps {
  children: ReactNode
}

export const PageHeaderProvider: React.FC<PageHeaderProviderProps> = ({
  children,
}) => {
  const stateRef = useRef<PageHeaderState>({
    customPageHeader: null,
    noPaddingBottom: false,
    headerBackgroundClassName: null,
  })
  const listenersRef = useRef(new Set<() => void>())

  const notify = useCallback(() => {
    listenersRef.current.forEach((listener) => {
      listener()
    })
  }, [])

  const setCustomPageHeader = useCallback(
    (header: ReactNode | null) => {
      if (stateRef.current.customPageHeader === header) {
        return
      }

      stateRef.current = {
        ...stateRef.current,
        customPageHeader: header,
      }
      notify()
    },
    [notify],
  )

  const setNoPaddingBottom = useCallback(
    (value: boolean) => {
      if (stateRef.current.noPaddingBottom === value) {
        return
      }

      stateRef.current = {
        ...stateRef.current,
        noPaddingBottom: value,
      }
      notify()
    },
    [notify],
  )

  const setHeaderBackgroundClassName = useCallback(
    (className: string | null) => {
      if (stateRef.current.headerBackgroundClassName === className) {
        return
      }

      stateRef.current = {
        ...stateRef.current,
        headerBackgroundClassName: className,
      }
      notify()
    },
    [notify],
  )

  const controllerValue = useMemo(
    () => ({
      setCustomPageHeader,
      setNoPaddingBottom,
      setHeaderBackgroundClassName,
    }),
    [setCustomPageHeader, setNoPaddingBottom, setHeaderBackgroundClassName],
  )

  const storeValue = useMemo<PageHeaderStore>(
    () => ({
      getSnapshot: () => stateRef.current,
      setCustomPageHeader,
      setNoPaddingBottom,
      setHeaderBackgroundClassName,
      subscribe: (listener: () => void) => {
        listenersRef.current.add(listener)

        return () => {
          listenersRef.current.delete(listener)
        }
      },
    }),
    [setCustomPageHeader, setNoPaddingBottom, setHeaderBackgroundClassName],
  )

  return (
    <PageHeaderStoreContext.Provider value={storeValue}>
      <PageHeaderControllerContext.Provider value={controllerValue}>
        {children}
      </PageHeaderControllerContext.Provider>
    </PageHeaderStoreContext.Provider>
  )
}

export const usePageHeader = (): PageHeaderState => {
  const store = useContext(PageHeaderStoreContext)
  if (!store) {
    throw new Error("usePageHeader must be used within PageHeaderProvider")
  }

  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  )
}

export const usePageHeaderController = (): PageHeaderControllerContextType => {
  const context = useContext(PageHeaderControllerContext)
  if (!context) {
    throw new Error(
      "usePageHeaderController must be used within PageHeaderProvider",
    )
  }
  return context
}
